import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Fuel, ArrowLeft, Loader2 } from "lucide-react";

// A simple, safe Markdown-to-HTML converter (headings, paragraphs, lists, links,
// inline emphasis). No dangerous HTML is executed — we escape everything first.
function escapeHtml(s) {
  return s.replace(/[&<>"']/g, (c) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
  }[c]));
}
function renderMd(md) {
  if (!md) return "";
  let s = escapeHtml(md);
  s = s.replace(/^### (.*)$/gm, '<h3 class="text-xl font-bold mt-6 mb-2">$1</h3>');
  s = s.replace(/^## (.*)$/gm, '<h2 class="text-2xl font-bold mt-8 mb-3">$1</h2>');
  s = s.replace(/^# (.*)$/gm, '<h1 class="text-3xl font-black mt-8 mb-4">$1</h1>');
  s = s.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  s = s.replace(/\*(.+?)\*/g, "<em>$1</em>");
  s = s.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noopener" class="text-[#FF5A1F] hover:underline">$1</a>');
  // lists: consecutive "- x" lines
  s = s.replace(/(^|\n)((?:- .+\n?)+)/g, (m, p, list) => {
    const items = list.trim().split(/\n/).map((l) => l.replace(/^-\s+/, "").trim()).map((t) => `<li>${t}</li>`).join("");
    return `${p}<ul class="list-disc pl-6 space-y-1 my-3">${items}</ul>`;
  });
  // paragraphs (double newline)
  s = s
    .split(/\n{2,}/)
    .map((chunk) => (chunk.trim().startsWith("<") ? chunk : `<p class="my-3 leading-relaxed">${chunk.replace(/\n/g, "<br/>")}</p>`))
    .join("\n");
  return s;
}

export default function LegalPage() {
  const { slug } = useParams();
  const [doc, setDoc] = useState(null);
  const [operator, setOperator] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [{ data: d }, { data: op }] = await Promise.all([
          api.get(`/legal/${slug}`),
          api.get(`/legal/config/operator`),
        ]);
        setDoc(d);
        setOperator(op);
      } catch {
        setDoc(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white grain">
      <header className="border-b border-[#262626] sticky top-0 bg-[#0A0A0A]/80 backdrop-blur z-10">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="legal-home">
            <div className="w-9 h-9 bg-[#FF5A1F] flex items-center justify-center">
              <Fuel size={20} strokeWidth={2.5} className="text-black" />
            </div>
            <span className="font-display text-lg font-black tracking-tighter">FUELTRACK</span>
          </Link>
          <Link to="/" className="text-sm text-[#A3A3A3] hover:text-white inline-flex items-center gap-2">
            <ArrowLeft size={14} /> Back
          </Link>
        </div>
      </header>
      <div className="max-w-3xl mx-auto px-6 py-10">
        {loading ? (
          <div className="text-[#737373] flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Loading…</div>
        ) : !doc ? (
          <div>
            <h1 className="font-display text-4xl font-black tracking-tighter">Page not found</h1>
            <p className="text-[#A3A3A3] mt-3">This legal page hasn't been published yet.</p>
          </div>
        ) : (
          <article>
            <p className="ft-overline mb-2">LEGAL · v{(doc.version || "1").replace(/^v/i, "")}</p>
            <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter leading-none mb-4">
              {doc.title}
            </h1>
            <p className="text-xs text-[#737373] font-mono mb-8">
              Last updated: {String(doc.updated_at || "").slice(0, 10)}
              {operator?.jurisdiction ? ` · Jurisdiction: ${operator.jurisdiction}` : ""}
            </p>
            {doc.content_md ? (
              <div
                className="prose prose-invert prose-p:text-[#D4D4D4] prose-li:text-[#D4D4D4] max-w-none"
                data-testid={`legal-content-${slug}`}
                dangerouslySetInnerHTML={{ __html: renderMd(doc.content_md) }}
              />
            ) : (
              <div className="border border-dashed border-[#262626] p-8 text-center text-[#737373]">
                <p>The operator hasn't published this document yet.</p>
                {operator?.support_email && (
                  <p className="mt-2 text-xs">For any questions, please contact <a className="text-[#FF5A1F] hover:underline" href={`mailto:${operator.support_email}`}>{operator.support_email}</a></p>
                )}
              </div>
            )}
            {(operator?.legal_entity || operator?.registered_address) && (
              <footer className="mt-12 pt-6 border-t border-[#262626] text-xs text-[#737373] font-mono">
                {operator.legal_entity && <p>{operator.legal_entity}</p>}
                {operator.registered_address && <p className="mt-1">{operator.registered_address}</p>}
                {operator.grievance_contact && <p className="mt-2">Grievance: {operator.grievance_contact}</p>}
              </footer>
            )}
          </article>
        )}
      </div>
    </div>
  );
}
